/**
 * The current session as the shell sees it: who, which role, which parish, on which
 * device. Section 05 fills it from the `sesi_lokal` row of the device database (the
 * identity the app trusts offline, spec 15.2) through `auth/session.ts`; screens only
 * read it. `status` says whether anything is signed in and whether the PIN lock is on;
 * the AuthGuard (`auth/guard.tsx`) turns that into redirects.
 *
 * The exported shape of wave 3 is kept: `sesi` is always a `Sesi` (an empty one before a
 * login), `namaHeader`, `labelPeran`, `cacheNamaParoki` and `setPeran` (development only:
 * a display override so every shell variant can be checked without six accounts).
 *
 * Plan 10: `asli` is the person (the session controller writes it), `bekerjaDi` the parish an
 * admin keuskupan works in, and `sesi`, what every screen reads, is derived from both on every
 * write (`turunkan`). The switch (shell/PilihTempatKerja) moves it (`gantiTempatKerja`), keeps
 * its names current (`segarkanTempatKerja`) and leaves a parish that is gone
 * (`lepasTempatHilang`).
 */
import {
  format,
  isiUnit,
  type LangkahAkun,
  SEBUTAN_BAWAAN,
  type SebutanUnit,
  uiStrings,
} from '@satu/shared'
import { create } from 'zustand'
import { teksTingkat } from '../../lib/tingkat'
import { KEUSKUPAN, ROLE_LABEL, ROLES, type Role } from '../shell/nav'
import { browserStorage, type StorageLike } from './tampilan'
import { bacaTempatKerja, simpanTempatKerja, type TempatKerja } from './tempatKerja'

export type { TempatKerja }

export interface Sesi {
  nama: string
  peran: Role
  paroki: { nama: string; pendek: string } | null
  /** The lingkungan name of a ketua lingkungan; null for every other role. */
  lingkungan: string | null
  /** Supabase auth user id, equal to `pengguna.id`; empty before a login. */
  userId: string
  parokiId: string | null
  lingkunganId: string | null
  /** The stasi name of a ketua stasi (spec 2026-09-30, 7); null for every other role. */
  stasi: string | null
  stasiId: string | null
  /** The enrolled device (UUID v7); empty before a login. */
  deviceId: string
}

export type SesiStatus =
  /** The device database has not answered yet. */
  | 'memuat'
  /** No `sesi_lokal` row: the online login is required. */
  | 'tanpa_sesi'
  /** A session exists and the PIN lock is on (launch, or ten minutes idle). */
  | 'terkunci'
  | 'aktif'

/** The API reason codes that end the session and wipe the device (spec 15.2). */
export type AlasanPenghapusan = 'user_deactivated' | 'device_revoked' | 'user_revoked'

/** Kept for wave 3 callers; the development override no longer persists. */
// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const SESI_DEV_KEY = 'simu:sesi-dev'
/** The splash reads the cached header name from this key before React mounts. */
// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const PAROKI_CACHE_KEY = 'simu:paroki'

export const SESI_KOSONG: Sesi = {
  nama: '',
  peran: 'staf_paroki',
  paroki: null,
  lingkungan: null,
  userId: '',
  parokiId: null,
  lingkunganId: null,
  stasi: null,
  stasiId: null,
  deviceId: '',
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

export function isKeuskupan(role: Role): boolean {
  return KEUSKUPAN.includes(role)
}

/**
 * The role's words; a ketua lingkungan's in the word of their parish (Sebutan unit terkecil:
 * "Ketua Kombas"), a dekan's in the diocese's word for the middle level (release 16: "Ketua
 * wilayah"), while the stored role codes stay `ketua_lingkungan` and `dekan`.
 */
export function labelPeran(role: Role, sebutan: SebutanUnit = SEBUTAN_BAWAAN): string {
  return teksTingkat(isiUnit(ROLE_LABEL[role], sebutan))
}

/**
 * Header text: the parish name, its short form under 768 px, the diocese's name
 * (`header.keuskupan`, "Keuskupan Agats-Asmat") for diocese roles.
 */
export function namaHeader(sesi: Sesi, pendek = false): string {
  if (isKeuskupan(sesi.peran)) return uiStrings.header.keuskupan
  if (sesi.paroki === null) return 'SATU'
  return pendek ? sesi.paroki.pendek : sesi.paroki.nama
}

/** Plan 10: the person as the Admin Paroki of `tempat` when an admin keuskupan works there. */
export function sesiKerja(asli: Sesi, tempat: TempatKerja | null): Sesi {
  if (asli.peran !== 'admin_keuskupan' || tempat === null) return asli
  return {
    ...asli,
    peran: 'admin_paroki',
    paroki: { nama: tempat.nama, pendek: tempat.pendek },
    parokiId: tempat.parokiId,
    lingkungan: null,
    lingkunganId: null,
    stasi: null,
    stasiId: null,
  }
}

/** Plan 10: "(Admin Keuskupan)" after the parish's name while an admin keuskupan works in it. */
export function catatanHeader(s: Pick<SesiState, 'asli' | 'bekerjaDi'>): string | null {
  return s.asli.peran === 'admin_keuskupan' && s.bekerjaDi !== null
    ? format(uiStrings.bekerjaDi.catatan, { peran: labelPeran('admin_keuskupan') })
    : null
}

/**
 * Every write passes here. The person is the write's `asli` (or `sesi`, as before plan 10). A
 * write of the person takes their own stored choice (this device's copy, read again, as the
 * worker reads it with the scope); only an admin keuskupan works elsewhere.
 */
function turunkan(s: SesiState, bagian: Partial<SesiState>): Partial<SesiState> {
  const orang = bagian.asli ?? bagian.sesi
  if (orang === undefined && !('bekerjaDi' in bagian)) return bagian
  const asli = orang ?? s.asli
  let bekerjaDi: TempatKerja | null
  if (asli.peran !== 'admin_keuskupan') bekerjaDi = null
  else if ('bekerjaDi' in bagian) bekerjaDi = bagian.bekerjaDi ?? null
  else bekerjaDi = bacaTempatKerja(browserStorage(), asli.userId)
  return { ...bagian, asli, bekerjaDi, sesi: sesiKerja(asli, bekerjaDi) }
}

export interface SesiState {
  /**
   * Plan 10: the signed-in person as the server and this device's `sesi_lokal` know them. The
   * session controller writes it; Akun, Administrasi, the side panel's name and the lock read it.
   */
  asli: Sesi
  /** Plan 10: the parish an admin keuskupan works in; null: the diocese view, or another role. */
  bekerjaDi: TempatKerja | null
  /**
   * What every screen reads: `asli`, or for an admin keuskupan working in a parish that parish's
   * Admin Paroki (`sesiKerja`). Derived on every write; a write that names `sesi` and not `asli`
   * names the person, as before plan 10 (test set-ups, the development override).
   */
  sesi: Sesi
  status: SesiStatus
  /** The first install (the snapshot) has been completed on this device. */
  instalSelesai: boolean
  /** The Supabase session is gone or the API answered unauthorized: log in again when convenient. */
  perluMasukKembali: boolean
  /** A wipe reason waiting for "Mengerti"; the guard shows the dialog. */
  penghapusan: AlasanPenghapusan | null
  /** A login that succeeded online and now waits for its PIN (not yet in `sesi_lokal`). */
  menunggu: Sesi | null
  /**
   * Akun & keamanan (spec 2026-09-30, 6): the API refused this session for a step (a new
   * password, or the second step). The guard opens its screen; finishing it clears this.
   */
  langkahAkun: LangkahAkun | null
  /**
   * Akun › Kata sandi went through and the sync after it may meet the API's request for the
   * app's code: while this is up the code screen says why it asks, and Akun says the password
   * was changed when it opens again, then clears it (and so when it is left with no step
   * asked). A sign-in, a sign-out and a wipe clear it with the step.
   */
  sandiDiganti: boolean
  /**
   * The server changed this person's role or place (an admin's change, read from /sesi at
   * start, at the PIN or during the day): the device was cleared and downloads its data again,
   * and the download screen says why. Cleared when the download is done.
   */
  peranBerubah: boolean
  /** Development only: override the displayed role. */
  setPeran(peran: Role): void
}

export const useSesi = create<SesiState>((set) => ({
  asli: SESI_KOSONG,
  bekerjaDi: null,
  sesi: SESI_KOSONG,
  status: 'memuat',
  instalSelesai: false,
  perluMasukKembali: false,
  penghapusan: null,
  menunggu: null,
  langkahAkun: null,
  sandiDiganti: false,
  peranBerubah: false,
  setPeran(peran) {
    // The creator's `set` is not the store's `setState` below: it derives here, as every write.
    set((state) =>
      turunkan(state, {
        sesi: {
          ...state.asli,
          peran,
          lingkungan:
            peran === 'ketua_lingkungan' ? (state.asli.lingkungan ?? 'Lingkungan St. Maria') : null,
          stasi: peran === 'ketua_stasi' ? (state.asli.stasi ?? 'Stasi Syuru') : null,
        },
      }),
    )
  },
}))

// Plan 10: every write passes `turunkan`, so `sesi` always follows the person and the parish.
// zustand's `replace` is passed on: a whole state given is derived as well, then replaces.
const setDasar = useSesi.setState
useSesi.setState = ((
  bagian: SesiState | Partial<SesiState> | ((s: SesiState) => SesiState | Partial<SesiState>),
  replace?: boolean,
) => {
  const turun = (s: SesiState) => turunkan(s, typeof bagian === 'function' ? bagian(s) : bagian)
  if (replace === true) setDasar((s) => turun(s) as SesiState, true)
  else setDasar(turun)
}) as typeof useSesi.setState

/** Keep the splash's parish name current for the next launch. */
export function cacheNamaParoki(sesi: Sesi): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(
    PAROKI_CACHE_KEY,
    JSON.stringify({ nama: namaHeader(sesi), pendek: namaHeader(sesi, true) }),
  )
}

export interface GantiTempatDeps {
  /** The worker's `sesi:kerja`: answers the parish that applies. */
  kirim(parokiId: string | null): Promise<{ kerja: string | null }>
  storage: StorageLike | null
}

/**
 * The switch (plan 10): the worker first, so no screen reads the old parish under the new name;
 * then this device's copy for this person; then the screens. Only an admin keuskupan's choice
 * applies; one that lands after the person changed (a lock, a sign-in) is dropped. A choice this
 * device cannot keep (no storage, or full or blocked) takes the worker back to the diocese view
 * and the screens with it, so the two never disagree. Answers the parish now worked in (null:
 * the diocese view, or nothing applied).
 */
export async function gantiTempatKerja(
  tempat: TempatKerja | null,
  deps: GantiTempatDeps,
): Promise<TempatKerja | null> {
  const { asli } = useSesi.getState()
  if (asli.peran !== 'admin_keuskupan') return null
  const masihDia = () => {
    const kini = useSesi.getState().asli
    return kini.userId === asli.userId && kini.peran === 'admin_keuskupan'
  }
  const { kerja } = await deps.kirim(tempat?.parokiId ?? null)
  if (!masihDia()) return null
  const berlaku = tempat !== null && kerja === tempat.parokiId ? tempat : null
  if (!simpanTempatKerja(deps.storage, asli.userId, berlaku)) {
    if (kerja !== null) await deps.kirim(null)
    if (masihDia()) useSesi.setState({ bekerjaDi: null })
    return null
  }
  useSesi.setState({ bekerjaDi: berlaku })
  return berlaku
}

/**
 * The parish worked in, as the device's units name it now (`admin:tempat_kerja`, which the
 * switch reads again after every save or pulled page): a new name or Nama pendek (Pengaturan
 * paroki, which updates the person's own parish only) reaches the header and this person's
 * stored choice. The parish stays the same, so the worker is not told; a list without it, or the
 * same names, changes nothing.
 */
export function segarkanTempatKerja(
  daftar: readonly TempatKerja[],
  storage: StorageLike | null,
): void {
  const { asli, bekerjaDi } = useSesi.getState()
  if (bekerjaDi === null) return
  const kini = daftar.find((p) => p.parokiId === bekerjaDi.parokiId)
  if (kini === undefined || (kini.nama === bekerjaDi.nama && kini.pendek === bekerjaDi.pendek)) {
    return
  }
  const baru: TempatKerja = { parokiId: kini.parokiId, nama: kini.nama, pendek: kini.pendek }
  // A copy this device cannot keep still names the right parish: the next read fixes the names.
  simpanTempatKerja(storage, asli.userId, baru)
  useSesi.setState({ bekerjaDi: baru })
}

/** One return to the diocese at a time (the panel and Lainnya may both read the list). */
let melepas = false

/**
 * The parish worked in is no longer among the device's parishes: `admin:tempat_kerja` lists
 * others but not it (deleted in Administrasi, here or on another device). Back to the diocese
 * view through the switch, the worker first, so no screen keeps accepting work the server would
 * refuse. An empty list (nothing read yet) changes nothing. Answers true when the session went
 * back to the diocese; the caller says so and opens Beranda.
 */
export async function lepasTempatHilang(
  daftar: readonly TempatKerja[],
  deps: GantiTempatDeps,
): Promise<boolean> {
  const { bekerjaDi } = useSesi.getState()
  if (melepas || bekerjaDi === null || daftar.length === 0) return false
  if (daftar.some((p) => p.parokiId === bekerjaDi.parokiId)) return false
  melepas = true
  try {
    await gantiTempatKerja(null, deps)
  } finally {
    melepas = false
  }
  return useSesi.getState().bekerjaDi === null
}
